import React, { useState, useEffect } from 'react';
import { Plus, Search, Edit, Trash2, Calendar, ToggleLeft, ToggleRight, Eye, EyeOff } from 'lucide-react';
import { toast } from 'react-hot-toast';
import Layout from '../../components/Layout';
import { anoLetivoService } from '../../services/anoLetivoService';
import type { AnoLetivo } from '../../services/anoLetivoService';
import AnoLetivoFormModal from '../../components/AnoLetivoFormModal';
import ConfirmDeleteModal from '../../components/ConfirmDeleteModal';
import {
  Container,
  PageHeader as StyledPageHeader,
  PageTitle as StyledPageTitle,
  BaseButton,
  SearchInput as StyledSearchInput,
  Select,
  Flex,
  LoadingSpinner as StyledLoadingSpinner,
  EmptyState as StyledEmptyState,
  EmptyStateIcon,
  EmptyStateText,
  theme
} from '../../styles';
import {
  PageDescription,
  HeaderLeft,
  HeaderActions,
  FiltersContainer,
  FiltersRow,
  SearchInputWrapper,
  SearchIcon,
  AnosLetivosGrid,
  AnoLetivoCard,
  CardHeader,
  AnoTitle,
  CardActions,
  ActionButton,
  StatusContainer,
  StatusItem,
  StatusLabel,
  StatusBadge,
  ToggleButton,
  CardFooter
} from './AnosLetivosPage.styles';

const AnosLetivosPage: React.FC = () => {
  const [anosLetivos, setAnosLetivos] = useState<AnoLetivo[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<'todos' | 'ativo' | 'inativo'>('todos');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingAnoLetivo, setEditingAnoLetivo] = useState<AnoLetivo | null>(null);
  const [deletingAnoLetivo, setDeletingAnoLetivo] = useState<AnoLetivo | null>(null);

  const loadAnosLetivos = async () => {
    try {
      setLoading(true);
      const response = await anoLetivoService.listarAnosLetivos();
      setAnosLetivos(response);
    } catch (error: any) {
      toast.error('Erro ao carregar anos letivos');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAnosLetivos();
  }, []);

  const handleToggleAtivo = async (anoLetivo: AnoLetivo) => {
    try {
      await anoLetivoService.toggleAtivo(anoLetivo.id);
      toast.success(`Ano letivo ${anoLetivo.ano} ${!anoLetivo.ativo ? 'ativado' : 'desativado'} com sucesso!`);
      loadAnosLetivos();
    } catch (error: any) {
      toast.error('Erro ao alterar status ativo');
    }
  };

  const handleToggleMatriculas = async (anoLetivo: AnoLetivo) => {
    try {
      await anoLetivoService.toggleMatriculas(anoLetivo.id);
      toast.success(`Aceitação de matrículas ${!anoLetivo.aceitaMatriculas ? 'ativada' : 'desativada'} para ${anoLetivo.ano}!`);
      loadAnosLetivos();
    } catch (error: any) {
      toast.error('Erro ao alterar aceitação de matrículas');
    }
  };

  const handleToggleRematriculas = async (anoLetivo: AnoLetivo) => {
    try {
      await anoLetivoService.toggleRematriculas(anoLetivo.id);
      toast.success(`Aceitação de rematrículas ${!anoLetivo.aceitaRematriculas ? 'ativada' : 'desativada'} para ${anoLetivo.ano}!`);
      loadAnosLetivos();
    } catch (error: any) {
      toast.error('Erro ao alterar aceitação de rematrículas');
    }
  };

  const handleDelete = async () => {
    if (!deletingAnoLetivo) return;

    try {
      await anoLetivoService.excluirAnoLetivo(deletingAnoLetivo.id);
      toast.success('Ano letivo excluído com sucesso!');
      setDeletingAnoLetivo(null);
      loadAnosLetivos();
    } catch (error: any) {
      toast.error('Erro ao excluir ano letivo');
    }
  };

  const filteredAnosLetivos = (anosLetivos || []).filter(anoLetivo => {
    const matchesSearch = anoLetivo.ano.toString().includes(searchTerm);
    const matchesStatus = statusFilter === 'todos' ||
      (statusFilter === 'ativo' && anoLetivo.ativo) ||
      (statusFilter === 'inativo' && !anoLetivo.ativo);

    return matchesSearch && matchesStatus;
  });

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('pt-BR');
  };

  if (loading) {
    return (
      <Layout title="Anos Letivos">
        <Container>
          <Flex justify="center" align="center" style={{ minHeight: '200px' }}>
            <StyledLoadingSpinner />
          </Flex>
        </Container>
      </Layout>
    );
  }

  return (
    <Layout title="Anos Letivos">
      <Container>
        <StyledPageHeader>
          <HeaderLeft>
            <StyledPageTitle>
              <Calendar />
              Anos Letivos
            </StyledPageTitle>
            <PageDescription size="lg" color="secondary">
              Gerencie os anos letivos e suas configurações de matrículas
            </PageDescription>
          </HeaderLeft>
          <HeaderActions>
            <BaseButton variant="primary" onClick={() => setShowCreateModal(true)}>
              <Plus />
              Novo Ano Letivo
            </BaseButton>
          </HeaderActions>
        </StyledPageHeader>

        <FiltersContainer>
          <FiltersRow gap={theme.spacing[4]} align="center">
            <SearchInputWrapper>
              <SearchIcon>
                <Search />
              </SearchIcon>
              <StyledSearchInput
                type="text"
                placeholder="Buscar por ano..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={{ paddingLeft: '44px' }}
              />
            </SearchInputWrapper>
            <Select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as 'todos' | 'ativo' | 'inativo')}
              style={{ minWidth: '150px' }}
            >
              <option value="todos">Todos os status</option>
              <option value="ativo">Ativos</option>
              <option value="inativo">Inativos</option>
            </Select>
          </FiltersRow>
        </FiltersContainer>

        {filteredAnosLetivos.length === 0 ? (
          <StyledEmptyState>
            <EmptyStateIcon>
              <Calendar size={48} />
            </EmptyStateIcon>
            <EmptyStateText>
              {searchTerm || statusFilter !== 'todos'
                ? 'Nenhum ano letivo encontrado. Tente ajustar os filtros de busca.'
                : 'Nenhum ano letivo encontrado. Comece criando o primeiro ano letivo.'}
            </EmptyStateText>
            {!searchTerm && statusFilter === 'todos' && (
              <BaseButton variant="primary" onClick={() => setShowCreateModal(true)}>
                <Plus />
                Criar Primeiro Ano Letivo
              </BaseButton>
            )}
          </StyledEmptyState>
        ) : (
          <AnosLetivosGrid columns={3} gap={theme.spacing[6]}>
            {filteredAnosLetivos.map(anoLetivo => (
              <AnoLetivoCard key={anoLetivo.id}>
                <CardHeader justify="space-between" align="flex-start">
                  <AnoTitle>
                    <Calendar />
                    {anoLetivo.ano}
                  </AnoTitle>
                  <CardActions>
                    <ActionButton
                      variant="edit"
                      onClick={() => setEditingAnoLetivo(anoLetivo)}
                      title="Editar"
                    >
                      <Edit />
                    </ActionButton>
                    <ActionButton
                      variant="delete"
                      onClick={() => setDeletingAnoLetivo(anoLetivo)}
                      title="Excluir"
                    >
                      <Trash2 />
                    </ActionButton>
                  </CardActions>
                </CardHeader>

                <StatusContainer>
                  <StatusItem>
                    <StatusLabel>Status</StatusLabel>
                    <StatusBadge active={anoLetivo.ativo} size="sm">
                      {anoLetivo.ativo ? <Eye size={12} /> : <EyeOff size={12} />}
                      {anoLetivo.ativo ? 'Ativo' : 'Inativo'}
                    </StatusBadge>
                  </StatusItem>

                  <StatusItem>
                    <StatusLabel>Matrículas</StatusLabel>
                    <ToggleButton
                      isActive={anoLetivo.aceitaMatriculas}
                      onClick={() => handleToggleMatriculas(anoLetivo)}
                    >
                      {anoLetivo.aceitaMatriculas ? <ToggleRight /> : <ToggleLeft />}
                      {anoLetivo.aceitaMatriculas ? 'Ativo' : 'Inativo'}
                    </ToggleButton>
                  </StatusItem>

                  <StatusItem>
                    <StatusLabel>Rematrículas</StatusLabel>
                    <ToggleButton
                      isActive={anoLetivo.aceitaRematriculas}
                      onClick={() => handleToggleRematriculas(anoLetivo)}
                    >
                      {anoLetivo.aceitaRematriculas ? <ToggleRight /> : <ToggleLeft />}
                      {anoLetivo.aceitaRematriculas ? 'Ativo' : 'Inativo'}
                    </ToggleButton>
                  </StatusItem>

                  <StatusItem>
                    <StatusLabel>Ativar/Desativar</StatusLabel>
                    <ToggleButton
                      isActive={anoLetivo.ativo}
                      onClick={() => handleToggleAtivo(anoLetivo)}
                    >
                      {anoLetivo.ativo ? <ToggleRight /> : <ToggleLeft />}
                      {anoLetivo.ativo ? 'Ativo' : 'Inativo'}
                    </ToggleButton>
                  </StatusItem>
                </StatusContainer>

                <CardFooter>
                  Criado em {formatDate(anoLetivo.criadoEm)}
                </CardFooter>
              </AnoLetivoCard>
            ))}
          </AnosLetivosGrid>
        )}

        {showCreateModal && (
          <AnoLetivoFormModal
            onClose={() => setShowCreateModal(false)}
            onSubmit={(success) => {
              setShowCreateModal(false);
              if (success) loadAnosLetivos();
            }}
          />
        )}

        {editingAnoLetivo && (
          <AnoLetivoFormModal
            anoLetivo={editingAnoLetivo}
            onClose={() => setEditingAnoLetivo(null)}
            onSubmit={(success) => {
              setEditingAnoLetivo(null);
              if (success) loadAnosLetivos();
            }}
          />
        )}

        {deletingAnoLetivo && (
          <ConfirmDeleteModal
            title="Excluir Ano Letivo"
            message={`Tem certeza que deseja excluir o ano letivo ${deletingAnoLetivo.ano}? Esta ação não pode ser desfeita.`}
            onConfirm={handleDelete}
            onCancel={() => setDeletingAnoLetivo(null)}
          />
        )}
      </Container>
    </Layout>
  );
};

export default AnosLetivosPage;
